import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, X, Eye, EyeOff } from "lucide-react";
import { format } from "date-fns";
import type { DocumentWithCounts, DocumentVersion } from "@shared/schema";

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

interface VersionManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentWithCounts;
}

interface DocumentVersionWithDetails extends DocumentVersion {
  pageCount: number;
  processingStatus: string;
  processingProgress: number;
  commentCount: number;
  averageRating: number;
}

interface VersionItemProps {
  version: DocumentVersionWithDetails & { hidden?: boolean };
  isCurrentVersion: boolean;
  onDeleteClick: (id: string) => void;
  onVisibilityToggle: (id: string, hidden: boolean) => void;
}

function VersionItem({ version, isCurrentVersion, onDeleteClick, onVisibilityToggle }: VersionItemProps) {
  const isHidden = version.hidden;

  return (
    <div
      className={`group flex items-center gap-3 py-3 px-3 rounded-md border border-border hover:border-primary/20 hover:bg-accent/30 transition-all duration-150 ${isHidden ? 'opacity-50' : ''}`}
    >
      {/* Version circle */}
      <div
        className={`flex items-center justify-center w-8 h-8 rounded-full font-mono text-xs font-bold shrink-0 ${
          isCurrentVersion
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {version.version}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Name + badges */}
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-sm font-medium text-foreground">Version {version.version}</span>
          {isCurrentVersion && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 py-0 bg-primary/10 text-primary border-primary/20 font-medium">
              Current
            </Badge>
          )}
          {isHidden && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 py-0 bg-muted text-muted-foreground border-border font-medium">
              Hidden
            </Badge>
          )}
        </div>

        {/* Inline metadata row */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[11px] text-muted-foreground/70 leading-snug">
          <span>{format(new Date(version.createdAt), 'MMM d, yyyy')}</span>
          <span className="text-muted-foreground/30">·</span>
          <span>{version.pageCount}p</span>
          <span className="text-muted-foreground/30">·</span>
          <span>{formatBytes(version.size)}</span>
          {version.commentCount > 0 && (
            <>
              <span className="text-muted-foreground/30">·</span>
              <span>{version.commentCount} comments</span>
            </>
          )}
        </div>

        {/* Filename */}
        <p className="text-[11px] text-muted-foreground/50 truncate mt-0.5" title={version.originalFilename}>
          {version.originalFilename}
        </p>

        {/* Processing status */}
        {version.processingStatus !== 'completed' && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-amber-600 dark:text-amber-400 capitalize font-medium">
                {version.processingStatus}
              </span>
              <span className="font-mono text-[10px] text-amber-600/70 dark:text-amber-400/70">
                {version.processingProgress}%
              </span>
            </div>
            <Progress value={version.processingProgress} className="h-1 bg-amber-500/20" />
          </div>
        )}
      </div>

      {/* Action buttons — always visible on mobile, fade in on hover for desktop */}
      <div className="flex items-center gap-0.5 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onVisibilityToggle(version.id, !version.hidden);
          }}
          data-testid={`button-toggle-visibility-${version.version}`}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
        >
          {version.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick(version.id);
          }}
          data-testid={`button-delete-version-${version.version}`}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function VersionManagementDialog({ isOpen, onClose, document }: VersionManagementDialogProps) {
  const [deleteVersionId, setDeleteVersionId] = useState<string | null>(null);
  const [hideCurrentVersionId, setHideCurrentVersionId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: versions = [], isLoading } = useQuery<DocumentVersionWithDetails[]>({
    queryKey: ['/api/documents', document.id, 'versions', 'includeHidden'],
    queryFn: () => fetch(`/api/documents/${document.id}/versions?includeHidden=true`).then(res => res.json()),
    enabled: isOpen,
  });

  const selectedVersion = versions.find(v => v.id === deleteVersionId);

  const deleteVersionMutation = useMutation({
    mutationFn: async (versionId: string) => {
      return apiRequest('DELETE', `/api/documents/${document.id}/versions/${versionId}`);
    },
    onMutate: async (versionId) => {
      const versionsKey = ['/api/documents', document.id, 'versions', 'includeHidden'];
      await queryClient.cancelQueries({ queryKey: versionsKey });
      const previousVersions = queryClient.getQueryData(versionsKey);
      queryClient.setQueryData(versionsKey, (old: any[]) =>
        old?.filter(v => v.id !== versionId) || []
      );
      return { previousVersions };
    },
    onError: (err, versionId, context) => {
      queryClient.setQueryData(['/api/documents', document.id, 'versions', 'includeHidden'], context?.previousVersions);
      toast({ title: "Failed to delete version", variant: "destructive" });
    },
    onSuccess: () => {
      setDeleteVersionId(null);
      queryClient.invalidateQueries({ queryKey: ['/api/documents', document.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/documents'] });
      toast({ title: "Version deleted successfully" });
    },
  });

  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ versionId, hidden }: { versionId: string; hidden: boolean }) => {
      return apiRequest('PATCH', `/api/documents/${document.id}/versions/${versionId}/visibility`, { hidden });
    },
    onMutate: async ({ versionId, hidden }) => {
      const versionsKey = ['/api/documents', document.id, 'versions', 'includeHidden'];
      await queryClient.cancelQueries({ queryKey: versionsKey });
      const previousVersions = queryClient.getQueryData(versionsKey);
      queryClient.setQueryData(versionsKey, (old: any[]) =>
        old?.map(v => v.id === versionId ? { ...v, hidden } : v) || []
      );
      return { previousVersions };
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(['/api/documents', document.id, 'versions', 'includeHidden'], context?.previousVersions);
      toast({
        title: `Failed to ${variables.hidden ? 'hide' : 'show'} version`,
        variant: "destructive"
      });
    },
    onSuccess: (data, { hidden }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/documents', document.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/documents'] });
      toast({
        title: `Version ${hidden ? 'hidden' : 'shown'} successfully`
      });
    },
  });

  const confirmDeleteVersion = () => {
    if (deleteVersionId) {
      deleteVersionMutation.mutate(deleteVersionId);
    }
  };

  const handleVisibilityToggle = (versionId: string, hidden: boolean) => {
    const version = versions.find(v => v.id === versionId);
    if (hidden && version?.version === document.currentVersion) {
      setHideCurrentVersionId(versionId);
    } else {
      toggleVisibilityMutation.mutate({ versionId, hidden });
    }
  };

  const confirmHideCurrentVersion = () => {
    if (hideCurrentVersionId) {
      toggleVisibilityMutation.mutate({ versionId: hideCurrentVersionId, hidden: true });
      setHideCurrentVersionId(null);
    }
  };

  return (
    <>
      <Drawer
        open={isOpen}
        onOpenChange={onClose}
        modal={false}
        dismissible={false}
      >
        <DrawerContent
          className="h-screen max-h-screen flex flex-col [&>div:first-child]:hidden"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          {/* Sticky header — never scrolls away */}
          <DrawerHeader className="shrink-0 border-b border-border px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <DrawerTitle className="text-left text-base">Document Versions</DrawerTitle>
                <DrawerDescription className="text-xs mt-0.5">
                  {document.name}
                </DrawerDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </DrawerHeader>

          {/* Scrollable version list */}
          <div className="flex-1 overflow-y-auto px-4 pb-6 pt-2 space-y-1">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : (
              versions.map((version) => (
                <VersionItem
                  key={version.id}
                  version={version}
                  isCurrentVersion={version.version === document.currentVersion}
                  onDeleteClick={setDeleteVersionId}
                  onVisibilityToggle={handleVisibilityToggle}
                />
              ))
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Delete Version Confirmation Dialog */}
      <AlertDialog open={!!deleteVersionId} onOpenChange={() => setDeleteVersionId(null)}>
        <AlertDialogContent className="bg-background border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-destructive" />
              Delete Version {selectedVersion?.version}
              {selectedVersion?.version === document.currentVersion && (
                <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20">Current</Badge>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-sm">
              Are you sure you want to delete version {selectedVersion?.version}?
              This will permanently remove all pages, comments, and images for this version.
            </AlertDialogDescription>
            {selectedVersion?.version === document.currentVersion && (
              <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
                <div className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">
                  Deleting the current version
                </div>
                <div className="text-xs text-amber-600/80 dark:text-amber-400/80">
                  The next most recent version will automatically become the current version.
                </div>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteVersionId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                confirmDeleteVersion();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-version"
            >
              Delete Version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hide Current Version Confirmation Dialog */}
      <AlertDialog open={!!hideCurrentVersionId} onOpenChange={() => setHideCurrentVersionId(null)}>
        <AlertDialogContent className="bg-background border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <EyeOff className="w-4 h-4 text-amber-500" />
              Hide Current Version
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-sm">
              Are you sure you want to hide the current version that collaborators are viewing?
            </AlertDialogDescription>
            <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
              <div className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">
                Hiding the current version
              </div>
              <div className="text-xs text-amber-600/80 dark:text-amber-400/80">
                The next available visible version will become the current version. Collaborators will see this change immediately.
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setHideCurrentVersionId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                confirmHideCurrentVersion();
              }}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="button-confirm-hide-version"
            >
              Hide Version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
